import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getTours } from "@/api";
import TourCard from "@/components/TourCard";
import { useLang } from "@/i18n";
import { CATEGORIES } from "@/config";
import { useSearchParams } from "react-router-dom";

export default function ToursPage() {
  const { data = [], isLoading } = useQuery({ queryKey: ["tours"], queryFn: getTours });
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "all";

  const linkQuery = (() => {
    const q = new URLSearchParams();
    if (params.get("date")) q.set("date", params.get("date"));
    if (params.get("pax")) q.set("pax", params.get("pax"));
    const s = q.toString();
    return s ? `?${s}` : "";
  })();

  const filtered = useMemo(() => {
    if (activeCat === "all") return data;
    return data.filter((t) => t.category === activeCat);
  }, [data, activeCat]);

  return (
    <div data-testid="tours-page" className="pt-32 md:pt-40 pb-16">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <p className="eyebrow mb-5">{t("tours.kicker")}</p>
        <h1 className="font-serif-hc font-light text-5xl md:text-7xl leading-[1.02] text-[#F5F2EA]">
          {t("tours.title")}
        </h1>
        <p className="mt-6 max-w-2xl text-[#A3A095] text-lg leading-relaxed">{t("tours.sub")}</p>

        <div className="mt-12 flex flex-wrap gap-2 border-b border-[#1E2B25] pb-4">
          <button
            data-testid="cat-all"
            onClick={() => setParams({})}
            className={`text-[11px] tracking-[0.22em] uppercase px-4 py-2 border ${
              activeCat === "all" ? "border-[#C4A661] text-[#C4A661]" : "border-transparent text-[#A3A095] hover:text-[#F5F2EA]"
            }`}
          >
            {lang === "es" ? "Todos" : "All"}
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`cat-${c.key}`}
              onClick={() => setParams({ cat: c.key })}
              className={`text-[11px] tracking-[0.22em] uppercase px-4 py-2 border ${
                activeCat === c.key ? "border-[#C4A661] text-[#C4A661]" : "border-transparent text-[#A3A095] hover:text-[#F5F2EA]"
              }`}
            >
              {lang === "es" ? c.es : c.en}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-[#0C1310] border border-[#1E2B25] aspect-[4/5] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {filtered.map((tour, i) => <TourCard key={tour.id} tour={tour} index={i} linkQuery={linkQuery} />)}
            {filtered.length === 0 && (
              <p className="col-span-full text-[#A3A095]">{lang === "es" ? "Pronto agregaremos experiencias en esta categoría." : "We'll add experiences in this category soon."}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
